// ─── De dónde sale cada exigencia del marco CAP ──────────────────────────────
//
// EL PROBLEMA QUE RESUELVE. El asistente sabía decir «te faltan 3 TCU en
// Hermenéutica» y no sabía decir POR QUÉ 3, ni dónde lo dice TMAI. Un número sin
// su norma no se puede discutir: quien lo lee no sabe si es una regla de TMAI,
// una decisión del centro o un error del sistema. Y cuando se le pedía citar,
// el único documento que tenía era el marco serializado como JSON, así que citó
// `§ tcuStandard` — una clave de JSON. No desobedeció: no había otra cosa.
//
// LA FORMA VIENE DEL MARCO DE MEMBRESÍA, que ya lo hacía bien: cada criterio
// lleva un `sourceRef` como
// `'Statement of Intent (mar-2022), §3 — Six Step Process, step 2'`
// (`tmai-membership-2022-03.ts`). Acá se hace lo mismo para el CAP.
//
// LAS SECCIONES NO SE INVENTAN. Salen de la numeración real de los `.docx`, que
// `scripts/transcribe-tmai-cap-docs.py` transcribe a
// `__docs/SEED-tmai-cap-framework-2022-03.md`, y un test de contrato comprueba
// que cada `sourceRef` de acá apunte a una sección que EXISTE en esa
// transcripción. Una cita rota rompe el CI en vez de llegar a una pantalla.
/** Cómo se nombra cada documento en una cita. Coincide con los encabezados de
 *  la transcripción, que es lo que el modelo tiene delante. */
export const CAP_SOURCE_DOCUMENTS = {
    general: 'TMAI CAP General Instructions (mar-2022)',
    BSC: 'TMAI BSC CAP Specification (mar-2022)',
    DEP: 'TMAI DEP CAP Specification (mar-2022)',
};
/** Las once secciones A–K que las DOS especificaciones comparten.
 *
 *  Son las mismas letras que usa el generador de propuestas, así que una cita
 *  cae exactamente en la sección que el centro está llenando — y no en un lugar
 *  del documento que tiene que salir a buscar. */
export const CAP_SPEC_SECTIONS = {
    overview: 'A — Introduction & Overview of Program Purpose',
    programOutcomes: 'B — Program-level Learning Outcomes (PLOs)',
    entranceRequirements: 'C — Minimum Student Entrance Requirements',
    instructionalLanguage: 'D — Instructional Language',
    coreCoursework: 'E — Core Coursework Requirements',
    electives: 'F — Elective Coursework Requirements',
    deliveryFormat: 'G — Instructional Delivery Format',
    timeToComplete: 'H — Typical Time to Complete',
    facultyRequirements: 'I — Program Faculty Requirements',
    assessment: 'J — Assessment of Learning',
    spiritualHealth: 'K — Assuring Students’ Continued Spiritual Health',
};
/** Las secciones de las instrucciones generales que el sistema usa. No están
 *  todas: solo las que respaldan algún dato que el asistente puede dar. */
export const CAP_GENERAL_SECTIONS = {
    designPrinciples: 'I — Overarching CAP Design Principles',
    capOverview: 'II — Overview of Current TMAI CAPs',
    proposalOrganization: 'III.A — Proposal Organization',
    programOutcomes: 'III.B — Program-level Learning Outcomes',
    courseNumbering: 'III.C — Course labeling and Numbering',
    creditUnits: 'III.D — TMAI Credit Units',
    courseCatalogDescriptions: 'III.E — Course Catalog Descriptions',
    entranceRequirements: 'III.F — Student Academic Entrance Requirements',
    facultyResumes: 'III.G — Faculty Resumes & Curriculum Vitae',
    spiritualHealth: 'III.H — Assuring Students’ Continuing Spiritual Health',
    alignmentMatrices: 'III.I — Curriculum Alignment Matrices',
    ccdTemplate: 'Attachment 2 — Developing Course Catalog Descriptions',
    facultyCvGuidelines: 'Attachment 3 — Faculty Resume/Curriculum Vitae Guidelines',
    alignmentMatrixGuidelines: 'Attachment 4 — Curriculum Alignment Matrix Guidelines',
};
/** Cita a una sección de la especificación de un CAP. */
export function specRef(cap, section, detail) {
    const base = `${CAP_SOURCE_DOCUMENTS[cap]}, §${CAP_SPEC_SECTIONS[section]}`;
    return detail ? `${base}, ${detail}` : base;
}
/** Cita a una sección de las instrucciones generales. */
export function generalRef(section, detail) {
    const base = `${CAP_SOURCE_DOCUMENTS.general}, §${CAP_GENERAL_SECTIONS[section]}`;
    return detail ? `${base}, ${detail}` : base;
}
/** Dónde dice TMAI lo que cada atestación no académica declara.
 *
 *  Son las cuatro que la auditoría NO puede calcular: alguien las declara. Que
 *  lleven su norma importa más acá que en ningún otro lado — quien atestigua
 *  tiene que poder leer contra qué está atestiguando. */
export const CAP_NON_ACADEMIC_SECTIONS = {
    instructional_language: 'instructionalLanguage',
    faculty_credentials: 'facultyRequirements',
    entrance_requirements: 'entranceRequirements',
    spiritual_health: 'spiritualHealth',
};
export function nonAcademicRef(cap, key) {
    return specRef(cap, CAP_NON_ACADEMIC_SECTIONS[key]);
}
/** Qué sección respalda cada interruptor del motor de auditoría.
 *
 *  Una regla que rechaza una propuesta sin poder decir de dónde sale es una
 *  regla que el centro sufre sin poder discutir. Dos de las cinco viven en las
 *  instrucciones generales (valen para todos los CAP) y tres en la
 *  especificación del CAP que se está auditando — de ahí que haga falta el
 *  código y no alcance un mapa plano. */
export function auditRuleRef(cap, rule) {
    switch (rule) {
        case 'requireEveryPloEvaluated':
        case 'requireEveryCourseContributesToAtLeastOnePlo':
            return generalRef('alignmentMatrices');
        case 'prerequisiteCapMustBeRecognizedIfStacked':
            return generalRef('capOverview');
        case 'subjectAreaTcuMustMeetMinimum':
            return specRef(cap, 'coreCoursework', 'Figure 3');
        case 'totalTcuMustMeetMinimum':
            return specRef(cap, 'coreCoursework');
    }
}
/** Todas las citas que el marco puede emitir, para que el test de contrato las
 *  compruebe contra la transcripción sin tener que recorrer el árbol de datos. */
export function allSectionCitations() {
    const out = [];
    for (const section of Object.keys(CAP_SPEC_SECTIONS)) {
        out.push(specRef('BSC', section), specRef('DEP', section));
    }
    for (const section of Object.keys(CAP_GENERAL_SECTIONS)) {
        out.push(generalRef(section));
    }
    return out;
}
//# sourceMappingURL=cap-tmai-source-refs.js.map